import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import AppHeader from '@shared-client/AppHeader.tsx';
import ThemeToggle from '../components/ThemeToggle.tsx';
import { BRIEF_TEMPLATE_URL, EXAMPLE_BRIEF_ZIP_URL } from '../lib/links.ts';
import AccountMenu from '../components/AccountMenu.tsx';

// The Guide (/guide): a short, self-serve help page for RKH staff — how to get
// content and images into the New-deck builder. Deliberately lean: Studio's
// planner reads the whole brief and structures it itself, so staff write
// naturally rather than learning a marker vocabulary. The page covers only what
// the planner genuinely can't infer (image naming, the Library, the intake
// choices) plus the practicalities. Plain web copy, read top-to-bottom; no
// fetch, no LLM, no props — it loads instantly. The header mirrors
// routes/Library.jsx so moving between browsing surfaces feels like one app.

const SECTIONS = [
  { id: 'how-it-works', label: 'How it works' },
  { id: 'content-file', label: 'Your content file' },
  { id: 'ai-tidy', label: 'Tidy your document with AI' },
  { id: 'images', label: 'Images' },
  { id: 'follow-words', label: 'How closely to follow your words' },
  { id: 'other-choices', label: 'The other choices' },
  { id: 'after-build', label: 'After it builds' },
  { id: 'checklist', label: 'Quick checklist' },
];

// The copyable reformatting prompt for section 3. Self-contained: the model a
// staff member pastes this into knows nothing about Studio, so the prompt
// carries the whole target shape (the same rules section 2 teaches) plus the
// guardrails that matter most when an LLM touches someone's content — never
// invent, never drop silently, flag what it left out.
const TIDY_PROMPT = `You are reformatting a rough document into a clean brief for an automated presentation builder. The builder reads the whole brief and turns each topic into roughly one slide, so the shape of the text decides the shape of the deck. Rewrite the document I give you into that shape.

Rules:
1. Keep every fact, figure, name, quote and claim from my document, in my wording wherever you can. Never invent, estimate or round numbers, never add sources I did not give you, and never add new content of your own.
2. Structure: a heading per section, and under each heading one short paragraph per topic. One topic becomes one slide. Lead each paragraph with its point in the first sentence. Group related points into one paragraph rather than many tiny ones.
3. Where my document gives a source for a claim, end that paragraph with it on its own line: "Source: ...". Do not attach sources to claims that had none.
4. Keep the variety that is already there: quotes stay as quotes with the person's name; a before/after or this-versus-that stays together in one paragraph with both figures; a sequence of steps or dates stays together as one numbered list; a price list or rate card stays together as one topic with one line per row (name, description, rate, hours, cost - whatever my document gives).
5. If I state how something should look, keep that as a line under its topic: "Slide type: ..." (timeline, comparison, big number, quote, cards, table, chart).
6. A list of more than six items: tighten it to the strongest points, or split it into two topics if the items genuinely split - and tell me which you did.
7. British spelling. No exclamation marks. No em-dashes.
8. Output plain text. At the end add two short lists: "Leftovers:" - anything from my document you cut or could not place, so I can decide; and "Suggested images:" - subjects from the content worth a photo, each with a descriptive filename like team-on-location.jpg. Write "none" if a list is empty.

Here is my document:`;

export default function Guide() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  function copyPrompt() {
    navigator.clipboard.writeText(TIDY_PROMPT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function logout() {
    await api.post('/api/logout');
    window.location.href = '/login';
  }

  return (
    <div className="guide-page">
      <AppHeader
        active="guide"
        onNavigate={navigate}
        actions={[
          { key: 'new', label: 'New deck', barLabel: '+ New deck', primary: true, onSelect: () => navigate('/new') },
          {
            key: 'theme',
            label: 'Appearance',
            ownRow: true,
            render: ({ inSheet }) => <ThemeToggle inSheet={inSheet} />,
          },
          {
            key: 'account',
            label: 'Sign out',
            ownRow: true,
            render: ({ inSheet }) => <AccountMenu inSheet={inSheet} onSignOut={logout} />,
          },
        ]}
      />

      <div className="guide">
        <nav className="guide__toc" aria-label="On this page">
          <span className="guide__toc-title">On this page</span>
          <ol className="guide__toc-list">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <main className="guide__content">
          <h1 className="guide__h1">Get the best deck from your content</h1>
          <p className="guide__lead">
            A one-minute read. Write your brief however reads naturally: Studio plans the sections and writes every
            slide, and you review and edit everything afterwards.
          </p>

          <section aria-labelledby="how-it-works">
            <h2 id="how-it-works" className="guide__h2">
              How it works
            </h2>
            <p>
              Drop a brief and images into <strong>New deck</strong>, answer a few questions, and Studio plans the deck,
              then writes every slide. You don't need to format the brief or learn any special layout: Studio reads the
              whole thing and structures it for you. Nothing is final: every slide is yours to edit afterwards.
            </p>
          </section>

          <section aria-labelledby="content-file">
            <h2 id="content-file" className="guide__h2">
              Your content file
            </h2>
            <p>
              Your brief can be a Google Doc, a Word file, a PDF or plain text, whatever you already have. Upload the
              file, or paste the text straight into New deck.
            </p>
            <ul className="guide__list">
              <li>
                <strong>No special formatting needed.</strong> Write one short paragraph per topic and lead with the
                point. Studio reads the whole document and turns each topic into about one slide: you don't need
                headings, markers or any special symbols.
              </li>
              <li>
                <strong>End each claim with its source.</strong> Finish the paragraph with <code>Source: …</code> and
                the attribution stays on the slide, and Studio also builds a sources page at the end of the deck
                automatically.
              </li>
              <li>
                <strong>Mix the ingredients.</strong> A brief that is all numbers becomes a deck that is all number
                slides. Add a quote (with the person's name), a comparison (give both sides), a short story or a
                timeline: each becomes its own kind of slide, and that mix is what makes the deck feel varied.
              </li>
              <li>
                <strong>Using headings anyway?</strong> In a longer document a heading per topic (a bold line in Word or
                Google Docs is fine) gives Studio natural section breaks. Keep the points that belong together under one
                heading and they stay on one slide; split one topic across lots of tiny headings and you'll get lots of
                small slides.
              </li>
              <li>
                <strong>Want a specific layout?</strong> If you have a strong opinion for one slide, add a line under
                its topic, such as <code>Slide type: timeline</code>. Studio uses it where the content fits. Handy ones:
                timeline, comparison, big number, quote, cards, table, chart.
              </li>
            </ul>
            <figure className="guide__example">
              <pre>{`AI adoption in agencies has gone mainstream. Seventy-two per cent of
agency staff now use generative AI in their daily work, up from 41%
a year ago. Source: AdAge Workforce Survey.

Programmatic advertising keeps growing. UK programmatic ad spend
reached £18.4bn in 2026, a 12% rise on the previous year. Source: IAB UK.

Creativity still matters most. As Sir John Hegarty of BBH puts it:
"Data tells you what happened. Creativity tells you why anyone
should care."`}</pre>
              <figcaption>
                Three paragraphs from a real brief: no formatting, one topic each, a source on every claim, and a quote
                to vary the pace. Each paragraph became one slide, every source was kept, and the quote became a
                full-screen quote slide.
              </figcaption>
              <div className="guide__example-links">
                <a href={EXAMPLE_BRIEF_ZIP_URL}>Download a full worked example</a> — a real brief plus its photos; drop
                the whole folder into New deck. Or copy the{' '}
                <a href={BRIEF_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">
                  blank brief template
                </a>{' '}
                if you like a ready-made shape.
              </div>
            </figure>
          </section>

          <section aria-labelledby="ai-tidy">
            <h2 id="ai-tidy" className="guide__h2">
              Optional: tidy your document with AI
            </h2>
            <p>
              Got rough notes, a long report or a messy export? Paste this prompt into Claude or ChatGPT, put your
              document after it, and you'll get back a brief in the shape Studio reads best — then upload that to{' '}
              <strong>New deck</strong>. Check the result before you use it, especially the <strong>Leftovers</strong>{' '}
              list at the end: that's anything the AI cut, and only you know if it mattered.
            </p>
            <figure className="guide__prompt">
              <div className="guide__prompt-head">
                <span className="guide__prompt-label">The reformatting prompt</span>
                <button type="button" className="btn btn--sm guide__copy" onClick={copyPrompt}>
                  {copied ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                  {copied ? 'Copied' : 'Copy prompt'}
                </button>
              </div>
              <pre>{TIDY_PROMPT}</pre>
            </figure>
          </section>

          <section aria-labelledby="images">
            <h2 id="images" className="guide__h2">
              Images
            </h2>
            <p>
              You don't place images yourself: Studio matches each photo to the slide whose subject it fits, features
              the best ones, and leaves the rest in the deck's assets to drop in by hand later. Tell it what it is
              looking at:
            </p>
            <ul className="guide__list">
              <li>
                <strong>Say what each picture shows</strong> in the New Deck wizard. Every image you add gets a line to
                describe it (&ldquo;Manchester site aerial&rdquo;), a part of your brief it belongs with, and a dial for
                how prominently to use it. It is optional, and it is the difference between Studio knowing the subject
                and guessing at it.
              </li>
              <li>
                <strong>Upload a deck and its own photos come too.</strong> Rebuild a PowerPoint or a PDF and Studio
                pulls the pictures out of it, skips the backgrounds and logos, and shows you what it found. Each one
                goes back on the slide it came from unless you say otherwise, and you can leave any of them out.
              </li>
              <li>
                <strong>Descriptive file names still help</strong>: <code>hydrothermal-vent.jpg</code> beats{' '}
                <code>IMG_2931.jpg</code> when you skip the descriptions.
              </li>
              <li>
                <strong>Quality:</strong> high-resolution, landscape works best for full-bleed; keep each under 10MB.
              </li>
              <li>
                <strong>RKH people and logos:</strong> don't upload them, pick them from the <strong>Library</strong>{' '}
                (the shared asset bank has staff portraits, logos and brand imagery).
              </li>
            </ul>
          </section>

          <section aria-labelledby="follow-words">
            <h2 id="follow-words" className="guide__h2">
              Choose how closely to follow your words
            </h2>
            <p>When you upload a brief, Studio asks how faithfully to use it:</p>
            <ul className="guide__list">
              <li>
                <strong>Strictly verbatim</strong>: your exact words, nothing added. Use for approved or sensitive copy.
              </li>
              <li>
                <strong>Light formatting</strong>: keeps all your words, just tidies and lays them out.
              </li>
              <li>
                <strong>Adapt &amp; expand</strong>: Studio reworks your content and fills gaps with accurate detail.
                Use when you have rough notes you want turned into a polished deck.
              </li>
            </ul>
            <p>
              When a brief is heavy, Studio also asks <strong>what to do if a slide gets too heavy</strong>:
            </p>
            <ul className="guide__list">
              <li>
                <strong>Split and keep everything</strong>: keeps every point, so the deck runs as long as the content
                needs. It still aims for one slide per topic, so group related points under one heading (above) to avoid
                lots of small slides.
              </li>
              <li>
                <strong>Tighten to fit</strong>: trims to the essentials to stay near your chosen length.
              </li>
            </ul>
          </section>

          <section aria-labelledby="other-choices">
            <h2 id="other-choices" className="guide__h2">
              The other choices
            </h2>
            <ul className="guide__list">
              <li>
                <strong>Length &amp; sections</strong>: let the planner follow your brief's structure, or set exact
                numbers. You review the plan before it builds. (Pasting notes with no file? You get a quick
                short/standard/deep and live-vs-read choice instead.)
              </li>
              <li>
                <strong>Audience</strong>: when you choose <strong>Adapt &amp; expand</strong>, pick the tone (team,
                client pitch, conference, workshop).
              </li>
              <li>
                <strong>Colour</strong>: one base colour, or a different accent per section.
              </li>
            </ul>
          </section>

          <section aria-labelledby="after-build">
            <h2 id="after-build" className="guide__h2">
              After it builds
            </h2>
            <p>It's a draft to refine:</p>
            <ul className="guide__list">
              <li>
                <strong>Edit tab</strong> for quick changes: text, swap an image, recolour, add or remove list items.
              </li>
              <li>
                <strong>Chat</strong> for bigger changes: restructure a slide, swap its layout.
              </li>
              <li>
                <strong>Recolour a section</strong>: changes all its slides to that colour (unless you'd overridden a
                slide).
              </li>
            </ul>
          </section>

          <section aria-labelledby="checklist">
            <h2 id="checklist" className="guide__h2">
              Quick checklist
            </h2>
            <div className="guide__dodont">
              <div className="guide__do">
                <h3 className="guide__h3">Do</h3>
                <ul className="guide__list">
                  <li>Write one short paragraph per topic; lead with the point.</li>
                  <li>
                    End every claim with <code>Source: …</code>
                  </li>
                  <li>Mix numbers with a quote, a comparison or a story.</li>
                  <li>Say what each picture shows when the wizard asks.</li>
                  <li>Pick RKH people and logos from the Library.</li>
                  <li>Use British spelling.</li>
                </ul>
              </div>
              <div className="guide__dont">
                <h3 className="guide__h3">Don't</h3>
                <ul className="guide__list">
                  <li>Overstuff one slide with a long list.</li>
                  <li>Send a wall of numbers and nothing else: you'll get a wall of number slides.</li>
                  <li>Split one topic across many tiny headings: it makes many small slides.</li>
                  <li>
                    Upload <code>IMG_2931.jpg</code>-style file names.
                  </li>
                  <li>Upload staff portraits or logos.</li>
                </ul>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
